import * as THREE from 'three';
import { ArrowType } from './ArrowType';
import { Enemy } from '../enemies/Enemy';

/**
 * Represents an arrow in the game
 */
export class ArrowEntity {
  // Arrow object
  private object: THREE.Object3D;
  
  // Arrow mesh
  private arrowBody: THREE.Mesh;
  private arrowHead: THREE.Mesh;
  
  // Arrow type
  private type: ArrowType;
  
  // Physics properties
  private velocity: THREE.Vector3 = new THREE.Vector3();
  private gravity: number = 9.8;
  private active: boolean = false;
  private lifeTime: number = 10; // seconds
  private currentLifeTime: number = 0;
  private hitEnemies: Set<Enemy> = new Set(); // Track enemies hit by THIS arrow
  
  // Damage values by arrow type
  private static DAMAGE_VALUES: Record<ArrowType, number> = {
    [ArrowType.NORMAL]: 10,
    [ArrowType.FIRE]: 18,
    [ArrowType.EXPLOSIVE]: 25,
    [ArrowType.POISON]: 12
  };
  
  // Current damage (can be modified by draw strength)
  private currentDamage: number;
  
  /**
   * Creates a new ArrowEntity
   * @param type The arrow type
   */
  constructor(type: ArrowType = ArrowType.NORMAL) {
    this.type = type;
    
    // Initialize with base damage
    this.currentDamage = ArrowEntity.DAMAGE_VALUES[type];
    
    // Create arrow object
    this.object = new THREE.Object3D();
    
    // Create arrow body
    const bodyGeometry = new THREE.CylinderGeometry(0.01, 0.01, 0.6, 8);
    const bodyMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
    this.arrowBody = new THREE.Mesh(bodyGeometry, bodyMaterial);
    
    // Create arrow head
    const headGeometry = new THREE.ConeGeometry(0.03, 0.1, 8);
    let headMaterial: THREE.Material;
    
    // Set material based on arrow type
    switch (type) {
      case ArrowType.FIRE:
        headMaterial = new THREE.MeshStandardMaterial({ color: 0xFF4500, emissive: 0xFF4500, emissiveIntensity: 0.5 });
        break;
      case ArrowType.EXPLOSIVE:
        headMaterial = new THREE.MeshStandardMaterial({ color: 0xFF0000 });
        break;
      case ArrowType.POISON:
        headMaterial = new THREE.MeshStandardMaterial({ color: 0x00FF00 });
        break;
      default:
        headMaterial = new THREE.MeshStandardMaterial({ color: 0x808080 });
        break;
    }
    
    this.arrowHead = new THREE.Mesh(headGeometry, headMaterial);
    this.arrowHead.position.y = 0.35;
    
    // Add parts to object
    this.object.add(this.arrowBody);
    this.object.add(this.arrowHead);
    
    // Hide arrow initially
    this.object.visible = false;
  }
  
  /**
   * Gets the arrow object
   * @returns The arrow object
   */
  public getObject(): THREE.Object3D {
    return this.object;
  }
  
  /**
   * Gets the arrow type
   * @returns The arrow type
   */
  public getType(): ArrowType {
    return this.type;
  }
  
  /**
   * Fires the arrow
   * @param position The starting position
   * @param direction The direction to fire in
   * @param speed The initial speed
   * @param drawStrengthMultiplier Optional multiplier for damage based on draw strength (0.0-1.0)
   */
  public fire(position: THREE.Vector3, direction: THREE.Vector3, speed: number, drawStrengthMultiplier: number = 1.0): void {
    // Set position
    this.object.position.copy(position);
    
    // Set velocity
    this.velocity.copy(direction).normalize().multiplyScalar(speed);
    
    // Set rotation to match direction
    this.object.lookAt(position.clone().add(direction));
    this.object.rotateX(Math.PI / 2); // Adjust for model orientation
    
    // Activate arrow
    this.active = true;
    this.currentLifeTime = 0;
    this.object.visible = true;
    this.hitEnemies.clear(); // Clear hit enemies when firing a new arrow
    
    // Apply draw strength multiplier to damage with a more dramatic curve
    // Minimum damage is 30% of base, maximum is 150% of base for a fully drawn bow
    const minDamageMultiplier = 0.3;
    const maxDamageMultiplier = 1.5;
    
    // Apply a power curve to make full draws more rewarding
    // drawStrength^2 creates a curve where the damage increases more rapidly as you approach full draw
    const drawStrengthSquared = drawStrengthMultiplier * drawStrengthMultiplier;
    const effectiveMultiplier = minDamageMultiplier + (maxDamageMultiplier - minDamageMultiplier) * drawStrengthSquared;
    
    // Calculate final damage
    this.currentDamage = ArrowEntity.DAMAGE_VALUES[this.type] * effectiveMultiplier;
    
    console.log(`Fired ${ArrowType[this.type]} arrow at speed ${speed.toFixed(2)} with ${(drawStrengthMultiplier * 100).toFixed(0)}% draw strength, damage: ${this.currentDamage.toFixed(1)} (${(effectiveMultiplier * 100).toFixed(0)}% of base)`);
  }
  
  /**
   * Updates the arrow
   * @param deltaTime Time since last update in seconds
   * @returns True if the arrow is still active, false if it should be removed
   */
  public update(deltaTime: number): boolean {
    if (!this.active) return false;
    
    // Update lifetime
    this.currentLifeTime += deltaTime;
    if (this.currentLifeTime >= this.lifeTime) {
      this.deactivate();
      return false;
    }
    
    // Apply gravity
    this.velocity.y -= this.gravity * deltaTime;
    
    // Update position
    const movement = this.velocity.clone().multiplyScalar(deltaTime);
    this.object.position.add(movement);
    
    // Update rotation to match velocity
    this.object.lookAt(this.object.position.clone().add(this.velocity));
    this.object.rotateX(Math.PI / 2); // Adjust for model orientation
    
    // Simple ground collision
    if (this.object.position.y < 0) {
      this.object.position.y = 0;
      this.handleImpact();
    }
    
    return this.active;
  }
  
  /**
   * Checks for collisions with enemies
   * @param enemies Array of enemies to check for collisions
   */
  public checkEnemyCollisions(enemies: Enemy[]): void {
    if (!this.active || enemies.length === 0) return;
    
    const arrowPosition = this.object.position.clone();
    const arrowDirection = this.velocity.clone().normalize();
    
    // Create a bounding sphere for the arrow (centered at the arrowhead)
    const arrowHeadPosition = arrowPosition.clone().add(new THREE.Vector3(0, 0.35, 0));
    const arrowRadius = 0.1; // Small collision radius for the arrow
    
    for (const enemy of enemies) {
      // Skip enemies that have already been hit by this arrow
      if (this.hitEnemies.has(enemy)) continue;
      
      // Skip inactive or dead enemies
      if (!enemy.isActiveEnemy() || enemy.isDying()) continue;
      
      const enemyPosition = enemy.getPosition();
      const enemyHeight = enemy.getHeight();
      const enemyRadius = 0.5; // Approximate collision radius for enemies
      
      // Check if the arrow is within the enemy's bounding cylinder
      const horizontalDistSq = 
        Math.pow(enemyPosition.x - arrowHeadPosition.x, 2) + 
        Math.pow(enemyPosition.z - arrowHeadPosition.z, 2);
      
      const isWithinRadius = horizontalDistSq <= Math.pow(enemyRadius + arrowRadius, 2);
      const isWithinHeight = 
        arrowHeadPosition.y >= 0 && 
        arrowHeadPosition.y <= enemyHeight;
      
      if (isWithinRadius && isWithinHeight) {
        // Hit detected!
        this.hitEnemies.add(enemy);
        
        // Apply damage based on arrow type
        this.applyDamageToEnemy(enemy);
        
        // Handle hit effects
        this.handleImpact();
        
        // For explosive arrows, we'll continue to check other enemies for area damage
        if (this.type !== ArrowType.EXPLOSIVE) {
          break;
        }
      }
    }
  }
  
  /**
   * Applies damage to an enemy based on arrow type
   * @param enemy The enemy to damage
   */
  private applyDamageToEnemy(enemy: Enemy): void {
    let damageAmount = this.currentDamage;
    
    // Apply special effects based on arrow type
    switch (this.type) {
      case ArrowType.FIRE:
        // Fire arrows do additional damage
        console.log(`Fire arrow hit enemy for ${damageAmount.toFixed(1)} damage!`);
        break;
        
      case ArrowType.EXPLOSIVE:
        // Explosive arrows do area damage
        console.log(`Explosive arrow hit enemy for ${damageAmount.toFixed(1)} damage!`);
        break;
        
      case ArrowType.POISON:
        // Poison arrows do damage over time (would need a status effect system)
        console.log(`Poison arrow hit enemy for ${damageAmount.toFixed(1)} damage!`);
        break;
        
      default:
        console.log(`Normal arrow hit enemy for ${damageAmount.toFixed(1)} damage!`);
        break;
    }
    
    // Apply damage to the enemy
    const killed = enemy.takeDamage(damageAmount);
    
    if (killed) {
      console.log('Enemy was defeated!');
    }
  }
  
  /**
   * Handles arrow impact
   */
  private handleImpact(): void {
    // Handle special effects based on arrow type
    switch (this.type) {
      case ArrowType.EXPLOSIVE:
        console.log("Explosive arrow impact!");
        // Create explosion effect
        this.createExplosion();
        // Deactivate immediately
        this.deactivate();
        break;
        
      case ArrowType.FIRE:
        console.log("Fire arrow impact!");
        // Fire arrows could start fires
        // Stick in place
        this.velocity.set(0, 0, 0);
        this.active = false; // Keep visible but stop updating
        break;
        
      case ArrowType.POISON:
        console.log("Poison arrow impact!");
        // Poison arrows could create a poison cloud
        // Stick in place
        this.velocity.set(0, 0, 0);
        this.active = false; // Keep visible but stop updating
        break;
        
      default:
        console.log("Arrow impact!");
        // Normal arrows just stick where they hit
        this.velocity.set(0, 0, 0);
        this.active = false; // Keep visible but stop updating
        break;
    }
  }
  
  /**
   * Creates an explosion effect
   */
  private createExplosion(): void {
    // This would be expanded with proper particle effects and area damage
    console.log('Explosion at', this.object.position);
  }
  
  /**
   * Deactivates the arrow
   */
  public deactivate(): void {
    this.active = false;
    this.object.visible = false;
    this.hitEnemies.clear(); // Clear hit enemies when deactivating
  }
  
  /**
   * Checks if the arrow is active
   * @returns True if the arrow is active, false otherwise
   */
  public isActive(): boolean {
    return this.active;
  }
  
  /**
   * Cleans up resources when the arrow is no longer needed
   */
  public dispose(): void {
    // Dispose of geometries
    if (this.arrowBody.geometry) {
      this.arrowBody.geometry.dispose();
    }
    
    if (this.arrowHead.geometry) {
      this.arrowHead.geometry.dispose();
    }
    
    // Dispose of materials
    if (this.arrowBody.material instanceof THREE.Material) {
      this.arrowBody.material.dispose();
    }
    
    if (this.arrowHead.material instanceof THREE.Material) {
      this.arrowHead.material.dispose();
    }
  }
} 